import { z } from 'zod';

import { ProductFulfillmentMode, ProductStatus } from '@nucleus-commerce/api-client/admin';

import { seoOverrideSchema } from '@/features/seo';
import { metadataEntrySchema } from '@/shared/forms/metadata-entry-schema';

import { weightFieldSchema } from './weight-schema';

const HANDLE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
/** `SkuRoot` in the backend: 40 characters, letters, digits, hyphens and underscores. */
const SKU_ROOT = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
const COUNTRY = /^[A-Za-z]{2}$/;

/** The backend caps lead times at 365 days. */
export const MAX_LEAD_TIME_DAYS = 365;

/** Units a dimension attribute value may carry (`valueJson.unit`). */
export const DIMENSION_UNITS = ['mm', 'cm', 'in', 'g', 'ct'] as const;
export type DimensionUnit = (typeof DIMENSION_UNITS)[number];

/** Whole non-negative days typed into a text input; blank means "not set". */
const dayCount = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d{1,3}$/.test(v), { error: 'products:validation.integer' })
  .refine((v) => v === '' || Number(v) <= MAX_LEAD_TIME_DAYS, {
    error: 'products:validation.leadTimeMax',
  });

const wordList = z.array(z.string().trim().min(1).max(60)).max(50);

/**
 * One slot per attribute, keyed by attribute id. Every data type gets the same shape so the
 * record schema stays static; the mapper picks the fields the attribute's dataType uses:
 * `ids` for select / multiselect, `text` for text, `html` for rich_text, `number` for number,
 * `number` + `unit` for dimension, `bool` for boolean.
 */
export const attributeValueSchema = z.object({
  ids: z.array(z.string()),
  text: z.string(),
  html: z.string(),
  number: z.string().trim(),
  unit: z.string(),
  bool: z.boolean(),
});

const SWATCH_COLOR = /^#[0-9a-fA-F]{6}$/;

export const optionSwatchSchema = z.object({
  color: z
    .string()
    .refine((v) => SWATCH_COLOR.test(v), { error: 'products:optionsCard.swatchColorFormat' })
    .optional(),
  mediaId: z.string().optional(),
});

export const optionValueSchema = z.object({
  /** The saved product option value's id; absent until the product is saved with it. */
  valueId: z.string().optional(),
  /** Select axes: a dictionary value. */
  attributeValueId: z.string().optional(),
  /** Number axes: a free value + label. */
  value: z.string().optional(),
  /** Shown to shoppers; for a dictionary value, blank means the dictionary label. */
  label: z.string().optional(),
  /** Undefined means "as the dictionary has it", null means "no swatch". */
  swatch: optionSwatchSchema.nullable().optional(),
});

export const optionAxisSchema = z.object({
  /** The saved product option's id (`useFieldArray` reserves `id`); absent for a new axis. */
  optionId: z.string().optional(),
  attributeId: z.string().min(1),
  /** Label shown to shoppers; blank means the attribute label. */
  name: z.string().trim().max(120, { error: 'products:validation.optionNameLength' }),
  values: z.array(optionValueSchema).min(1, { error: 'products:validation.optionNeedsValue' }),
});

export const faqItemSchema = z.object({
  question: z
    .string()
    .trim()
    .min(1, { error: 'products:validation.faqQuestionRequired' })
    .max(300, { error: 'products:validation.faqQuestionLength' }),
  answer: z
    .string()
    .trim()
    .min(1, { error: 'products:validation.faqAnswerRequired' })
    .max(4000, { error: 'products:validation.faqAnswerLength' }),
});

export const productFormSchema = z
  .object({
    title: z.string().trim().min(1, { error: 'products:validation.titleRequired' }).max(200),
    // Blank means "derive from the title" (see mappers); a refine keeps the message specific,
    // where `.regex().or(z.literal(''))` would surface a generic union error instead.
    handle: z
      .string()
      .trim()
      .max(120)
      .refine((v) => v === '' || HANDLE.test(v), { error: 'products:validation.handleFormat' }),
    subtitle: z.string().trim().max(200),
    vendor: z.string().trim().max(120),
    skuRoot: z
      .string()
      .trim()
      .max(40, { error: 'products:validation.skuRootLength' })
      .refine((v) => v === '' || SKU_ROOT.test(v), { error: 'products:validation.skuRootFormat' }),
    descriptionHtml: z
      .string()
      .trim()
      .max(50_000, { error: 'products:validation.descriptionLength' }),
    summary: z.string().trim().max(1000, { error: 'products:validation.summaryLength' }),
    faq: z.array(faqItemSchema).max(20, { error: 'products:validation.faqMax' }),
    seo: seoOverrideSchema,
    tags: wordList,
    searchKeywords: wordList,
    status: z.enum(ProductStatus),
    fulfillmentMode: z.enum(ProductFulfillmentMode),
    leadTimeDaysMin: dayCount,
    leadTimeDaysMax: dayCount,
    requiresDesignApproval: z.boolean(),
    weight: weightFieldSchema,
    hsCode: z.string().trim().max(20, { error: 'products:validation.hsCodeLength' }),
    countryOfOrigin: z
      .string()
      .trim()
      .refine((v) => v === '' || COUNTRY.test(v), { error: 'products:validation.country' }),
    metadata: z.array(metadataEntrySchema).superRefine((entries, ctx) => {
      const seen = new Set<string>();
      entries.forEach((entry, index) => {
        const key = entry.key.trim();
        if (key && seen.has(key)) {
          ctx.addIssue({
            code: 'custom',
            path: [index, 'key'],
            message: 'products:validation.metadataKeyDuplicate',
          });
        }
        seen.add(key);
      });
    }),
    /** Empty string means no product type. */
    productTypeId: z.string(),
    customizationIds: z
      .array(z.string())
      .max(50, { error: 'products:validation.customizationsMax' }),
    attributeValues: z.record(z.string(), attributeValueSchema),
    options: z.array(optionAxisSchema).max(6, { error: 'products:validation.maxOptions' }),
    /**
     * Only on the create page (`newProductForm`), where it starts null and is required: the price
     * the first save creates the variants at. A saved product's form doesn't carry it.
     */
    basePrice: z
      .object({ amount: z.number().int(), currency: z.string() })
      .nullable()
      .refine((v) => v !== null && v.amount >= 0, { error: 'products:validation.priceRequired' })
      .optional(),
  })
  .refine(
    (v) =>
      v.leadTimeDaysMin === '' ||
      v.leadTimeDaysMax === '' ||
      Number(v.leadTimeDaysMin) <= Number(v.leadTimeDaysMax),
    { path: ['leadTimeDaysMax'], error: 'products:validation.leadTimeOrder' },
  );

/** What react-hook-form holds while editing. */
export type ProductFormInput = z.input<typeof productFormSchema>;
/** What handleSubmit receives after validation. */
export type ProductFormOutput = z.output<typeof productFormSchema>;
export type AttributeFormValue = z.infer<typeof attributeValueSchema>;
export type OptionAxisForm = z.infer<typeof optionAxisSchema>;
export type OptionValueForm = z.infer<typeof optionValueSchema>;
export type OptionSwatchForm = z.infer<typeof optionSwatchSchema>;
export type FaqItemForm = z.infer<typeof faqItemSchema>;
